import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Zap, UserPlus } from "lucide-react";
import { Input } from "../../components/ui/input";
import { Button } from "../../components/ui/button";
import { useAuth } from "../../context/AuthContext";
import { useToast } from "../../hooks/use-toast";

export default function DashboardSignup() {
  const nav = useNavigate();
  const { signup } = useAuth();
  const { toast } = useToast();
  const [form, setForm] = useState({ name: "", email: "", password: "" });
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (form.password.length < 6) {
      toast({ title: "Password too short", description: "Use at least 6 characters." });
      return;
    }
    setBusy(true);
    try {
      const u = await signup(form.name.trim(), form.email.trim(), form.password);
      toast({ title: "Welcome to FortressPoint", description: u.role === "admin" ? "You are the first user — promoted to admin." : "Account created." });
      nav("/dashboard", { replace: true });
    } catch (err) {
      const msg = err?.response?.data?.detail || "Signup failed";
      toast({ title: "Could not create account", description: String(msg) });
    } finally {
      setBusy(false);
    }
  };

  const cls = "bg-white/[0.03] border-white/10 focus-visible:border-cyan-400/50 focus-visible:ring-cyan-400/20 text-white placeholder:text-slate-500 h-11";

  return (
    <div className="min-h-screen hero-bg relative overflow-hidden flex items-center justify-center px-6">
      <div className="absolute inset-0 stars" />
      <div className="absolute inset-0 grid-overlay" />

      <div className="relative w-full max-w-md">
        <Link to="/" className="flex items-center gap-3 justify-center mb-8">
          <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-sky-500 to-cyan-400 flex items-center justify-center shadow-lg shadow-cyan-500/25">
            <Zap className="w-6 h-6 text-white" strokeWidth={2.5} />
          </div>
          <div className="leading-tight">
            <div className="font-display font-bold text-white text-lg">FortressPoint</div>
            <div className="text-[11px] tracking-widest uppercase text-cyan-300/80">Admin Dashboard</div>
          </div>
        </Link>

        <form onSubmit={submit} className="glass rounded-2xl p-8">
          <h1 className="font-display text-2xl font-bold text-white">Create your account</h1>
          <p className="mt-1 text-sm text-slate-400">First signup is auto-promoted to admin.</p>

          <div className="mt-6 space-y-3">
            <label className="block">
              <span className="block text-[11px] uppercase tracking-widest text-slate-400 font-semibold mb-2">Full Name</span>
              <Input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Jane Doe" className={cls} />
            </label>
            <label className="block">
              <span className="block text-[11px] uppercase tracking-widest text-slate-400 font-semibold mb-2">Email</span>
              <Input type="email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="you@company.com" className={cls} />
            </label>
            <label className="block">
              <span className="block text-[11px] uppercase tracking-widest text-slate-400 font-semibold mb-2">Password</span>
              <Input type="password" required minLength={6} value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} placeholder="At least 6 characters" className={cls} />
            </label>
          </div>

          <Button type="submit" disabled={busy} className="mt-6 w-full h-12 btn-primary rounded-xl font-semibold border-0">
            <UserPlus className="w-4 h-4 mr-2" /> {busy ? "Creating..." : "Create Account"}
          </Button>

          <p className="mt-5 text-center text-sm text-slate-400">
            Already have an account?{" "}
            <Link to="/dashboard/login" className="text-cyan-300 hover:text-cyan-200 font-semibold">Sign in</Link>
          </p>
        </form>
      </div>
    </div>
  );
}
